/**
 * AdminBroadcast — universal search + recipient picker + dual-channel push.
 * Super-admin can broadcast a notification to any user/tenant in one click.
 * Also has a Cause List Dispatch tab for WhatsApp + Email + Push delivery.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Send, Search, Smartphone, Globe, Loader2, CheckCircle2, BellRing,
    MessageSquare, Mail, Bell, ChevronDown, Users, Building2, XCircle
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

export default function AdminBroadcast() {
    const [tab, setTab] = useState("push"); // "push" | "causelist"

    return (
        <div className="space-y-6">
            <PageHeader title="Broadcast &amp; Dispatch"
                subtitle="Push broadcasts to users · Cause-list delivery via WhatsApp, Email &amp; Push"
                icon={<BellRing className="w-8 h-8" strokeWidth={1.5}/>}/>

            {/* Tab switcher */}
            <div className="flex border-b border-border gap-0">
                <TabBtn active={tab === "push"} onClick={() => setTab("push")} icon={<BellRing className="w-3.5 h-3.5"/>} label="Push Broadcast"/>
                <TabBtn active={tab === "causelist"} onClick={() => setTab("causelist")} icon={<MessageSquare className="w-3.5 h-3.5"/>} label="Cause List Dispatch"/>
            </div>

            {tab === "push" && <PushBroadcastPanel/>}
            {tab === "causelist" && <CauseListDispatchPanel/>}
        </div>
    );
}

function TabBtn({ active, onClick, icon, label }) {
    return (
        <button onClick={onClick}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.14em] border-b-2 transition-colors
                ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {icon}{label}
        </button>
    );
}

// ─── Push Broadcast Panel (unchanged logic) ────────────────────────────────
function PushBroadcastPanel() {
    const [q, setQ] = useState("");
    const [audience, setAudience] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [link, setLink] = useState("/app");
    const [sendWeb, setSendWeb] = useState(true);
    const [sendMobile, setSendMobile] = useState(true);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);

    const load = useCallback(async (term) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/broadcast/audience${term ? `?q=${encodeURIComponent(term)}` : ""}`);
            setAudience(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/broadcast/history?limit=20");
            setHistory(data?.rows || []);
        } catch (_) {}
    }, []);

    useEffect(() => { load(""); loadHistory(); }, [load, loadHistory]);
    useEffect(() => { const t = setTimeout(() => load(q), 400); return () => clearTimeout(t); }, [q, load]);

    const toggle = (uid) => setSelected(s => { const n = new Set(s); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
    const toggleAllVisible = () => {
        const vis = (audience?.users || []).map(u => u.user_id);
        const allSelected = vis.every(id => selected.has(id));
        const n = new Set(selected);
        if (allSelected) vis.forEach(id => n.delete(id)); else vis.forEach(id => n.add(id));
        setSelected(n);
    };

    const send = async () => {
        if (!title.trim() || !body.trim()) { toast.error("Title and body required"); return; }
        if (selected.size === 0) { toast.error("Select at least one recipient"); return; }
        if (!sendWeb && !sendMobile) { toast.error("Enable at least one channel"); return; }
        if (!window.confirm(`Broadcast "${title}" to ${selected.size} user(s)?`)) return;
        setSending(true);
        try {
            const r = await api.post("/admin/broadcast/send", {
                user_ids: [...selected], tenant_ids: [], title, body, link,
                send_to_web: sendWeb, send_to_mobile: sendMobile,
            });
            toast.success(r.data?.message || `Broadcasting to ${r.data?.recipients} users`);
            setTitle(""); setBody(""); setSelected(new Set());
            setTimeout(loadHistory, 4000);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSending(false); }
    };

    const kpi = audience?.kpi;
    const users = audience?.users || [];
    const allVisSelected = users.length > 0 && users.every(u => selected.has(u.user_id));

    return (
        <div className="space-y-6">
            {kpi && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="broadcast-kpi">
                    <Tile label="Total users" value={kpi.total_users}/>
                    <Tile label="Web enabled" value={`${kpi.users_with_web} (${kpi.web_enable_rate_pct}%)`} accent="text-emerald-400"/>
                    <Tile label="Mobile enabled" value={`${kpi.users_with_mobile} (${kpi.mobile_enable_rate_pct}%)`} accent="text-emerald-400"/>
                    <Tile label="Any push enabled" value={`${kpi.users_with_any} (${kpi.any_enable_rate_pct}%)`} accent="text-primary"/>
                </div>
            )}
            <div className="bg-muted border border-border p-5 space-y-3">
                <div className="overline text-primary">Compose</div>
                <input type="text" placeholder="Title" maxLength={80} value={title} onChange={e => setTitle(e.target.value)} data-testid="broadcast-title"
                    className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                <textarea placeholder="Message body" maxLength={300} rows={2} value={body} onChange={e => setBody(e.target.value)} data-testid="broadcast-body"
                    className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                <input type="text" placeholder="Deep link (default /app)" maxLength={200} value={link} onChange={e => setLink(e.target.value)} data-testid="broadcast-link"
                    className="w-full bg-background border border-border px-3 py-2 text-xs text-muted-foreground focus:border-primary outline-none"/>
                <div className="flex flex-wrap gap-3 items-center">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={sendWeb} onChange={e => setSendWeb(e.target.checked)} data-testid="broadcast-channel-web" className="accent-primary"/>
                        <Globe className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/> Web browsers
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={sendMobile} onChange={e => setSendMobile(e.target.checked)} data-testid="broadcast-channel-mobile" className="accent-primary"/>
                        <Smartphone className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/> Mobile
                    </label>
                    <button type="button" onClick={async () => {
                        try {
                            const { data } = await api.post("/admin/broadcast/test-self", {});
                            if (data?.devices === 0) toast.error("No paired devices. Enable notifications on this device first.");
                            else toast.success(`Test push sent to your ${data.devices} device(s).`);
                        } catch (e) { toast.error(apiError(e)); }
                    }} data-testid="broadcast-test-self"
                    className="text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/[0.08]">
                        ⚡ Test push to me
                    </button>
                    <div className="flex-1 text-right">
                        <button onClick={send} disabled={sending || selected.size === 0} data-testid="broadcast-send-btn"
                            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary/80 disabled:opacity-30">
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                            {sending ? "Queuing…" : `Send to ${selected.size} user${selected.size === 1 ? "" : "s"}`}
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                <div className="relative">
                    <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5}/>
                    <input type="text" placeholder="Search users by name, email, or firm name…" value={q} onChange={e => setQ(e.target.value)} data-testid="broadcast-search"
                        className="w-full bg-muted border border-border pl-10 pr-3 py-2.5 text-sm focus:border-primary outline-none"/>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{audience?.total_matched ?? 0} users matched · {selected.size} selected</span>
                    <button onClick={toggleAllVisible} className="text-primary hover:underline" data-testid="broadcast-toggle-all">
                        {allVisSelected ? "Deselect all visible" : "Select all visible"}
                    </button>
                </div>
                <div className="overflow-x-auto bg-muted border border-border max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-background sticky top-0 text-muted-foreground uppercase tracking-[0.16em] text-[10px]">
                            <tr>
                                <th className="text-left p-3 w-10">✓</th>
                                <th className="text-left p-3">User</th>
                                <th className="text-left p-3">Firm</th>
                                <th className="text-left p-3">Role</th>
                                <th className="text-center p-3">Desktop</th>
                                <th className="text-center p-3">Mobile</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 inline-block animate-spin"/></td></tr>}
                            {!loading && users.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No users match — try a different search.</td></tr>}
                            {users.map(u => (
                                <tr key={u.user_id} className={`border-t border-border cursor-pointer hover:bg-background/40 ${selected.has(u.user_id) ? "bg-primary/5" : ""}`}
                                    onClick={() => toggle(u.user_id)} data-testid={`broadcast-user-${u.user_id}`}>
                                    <td className="p-3"><input type="checkbox" checked={selected.has(u.user_id)} readOnly className="accent-primary pointer-events-none"/></td>
                                    <td className="p-3"><div className="text-foreground">{u.name}</div><div className="text-muted-foreground">{u.email}</div></td>
                                    <td className="p-3 text-muted-foreground">{u.tenant_name}</td>
                                    <td className="p-3 text-muted-foreground">{u.role}</td>
                                    <td className="p-3 text-center">{u.web_devices > 0 ? <CheckCircle2 className="w-3.5 h-3.5 inline-block text-emerald-400"/> : <span className="text-muted-foreground">—</span>}</td>
                                    <td className="p-3 text-center">{u.mobile_devices > 0 ? <CheckCircle2 className="w-3.5 h-3.5 inline-block text-emerald-400"/> : <span className="text-muted-foreground">—</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {history.length > 0 && (
                <div>
                    <div className="overline text-primary mb-2">Recent broadcasts</div>
                    <div className="bg-muted border border-border overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-background/30 text-muted-foreground uppercase tracking-[0.16em] text-[10px]">
                                <tr>
                                    <th className="text-left p-3">When</th>
                                    <th className="text-left p-3">Title</th>
                                    <th className="text-right p-3">Recipients</th>
                                    <th className="text-right p-3">Desktop</th>
                                    <th className="text-right p-3">Mobile</th>
                                    <th className="text-right p-3">Skipped</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h, i) => (
                                    <tr key={i} className="border-t border-border" data-testid={`broadcast-history-${i}`}>
                                        <td className="p-3 font-mono text-[11px]">{new Date(h.created_at).toLocaleString()}</td>
                                        <td className="p-3 text-foreground">{h.title}</td>
                                        <td className="p-3 text-right font-mono">{h.user_count}</td>
                                        <td className="p-3 text-right text-emerald-400 font-mono">{h.sent_web}</td>
                                        <td className="p-3 text-right text-emerald-400 font-mono">{h.sent_mobile}</td>
                                        <td className="p-3 text-right text-amber-400 font-mono">{h.skipped_no_device}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Cause List Dispatch Panel ─────────────────────────────────────────────
function CauseListDispatchPanel() {
    const tomorrow = (() => {
        const d = new Date(); d.setDate(d.getDate() + 1);
        return d.toISOString().split("T")[0];
    })();

    const [targetMode, setTargetMode] = useState("all");   // "all" | "selected"
    const [tenants, setTenants] = useState([]);
    const [selectedTenants, setSelectedTenants] = useState(new Set());
    const [tenantsLoading, setTenantsLoading] = useState(false);
    const [date, setDate] = useState(tomorrow);
    const [chWA, setChWA] = useState(true);
    const [chEmail, setChEmail] = useState(true);
    const [chPush, setChPush] = useState(true);
    const [dispatching, setDispatching] = useState(false);
    const [results, setResults] = useState(null);
    const [tenantSearch, setTenantSearch] = useState("");

    useEffect(() => {
        if (targetMode === "selected" && tenants.length === 0) {
            setTenantsLoading(true);
            api.get("/tenants").then(r => setTenants(r.data || [])).catch(() => {}).finally(() => setTenantsLoading(false));
        }
    }, [targetMode, tenants.length]);

    const filteredTenants = tenants.filter(t =>
        !tenantSearch || (t.name || "").toLowerCase().includes(tenantSearch.toLowerCase())
    );

    const toggleTenant = (id) => setSelectedTenants(s => {
        const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });

    const dispatch = async () => {
        if (!chWA && !chEmail && !chPush) { toast.error("Select at least one channel"); return; }
        if (targetMode === "selected" && selectedTenants.size === 0) { toast.error("Select at least one tenant"); return; }
        const label = targetMode === "all" ? "ALL tenants" : `${selectedTenants.size} tenant(s)`;
        if (!window.confirm(`Send cause list for ${date} to ${label} via ${[chWA && "WhatsApp", chEmail && "Email", chPush && "Push"].filter(Boolean).join(" + ")}?`)) return;
        setDispatching(true); setResults(null);
        try {
            const { data } = await api.post("/admin/cause-list/dispatch", {
                target: targetMode,
                tenant_ids: targetMode === "selected" ? [...selectedTenants] : [],
                date,
                whatsapp: chWA, email: chEmail, push: chPush,
            });
            setResults(data);
            toast.success(`Dispatched to ${data.ok_count}/${data.tenant_count} tenants`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setDispatching(false); }
    };

    return (
        <div className="space-y-6">
            {/* Config card */}
            <div className="bg-muted border border-border p-5 space-y-5">
                <div className="overline text-primary">Cause List Dispatch</div>
                <p className="text-xs text-muted-foreground">
                    Manually send the cause list to tenants via the selected channels.
                    WhatsApp is sent via Deropo. Email via your configured SMTP.
                    Push goes to all active firm users.
                </p>

                {/* Date */}
                <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cause List Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        data-testid="cl-dispatch-date"
                        className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                </div>

                {/* Channels */}
                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Channels</label>
                    <div className="flex flex-wrap gap-4">
                        <ChannelToggle icon={<MessageSquare className="w-3.5 h-3.5"/>} label="WhatsApp (Deropo)" checked={chWA} onChange={setChWA} testId="cl-ch-wa" color="text-emerald-400"/>
                        <ChannelToggle icon={<Mail className="w-3.5 h-3.5"/>} label="Email" checked={chEmail} onChange={setChEmail} testId="cl-ch-email" color="text-blue-400"/>
                        <ChannelToggle icon={<Bell className="w-3.5 h-3.5"/>} label="Push Notification" checked={chPush} onChange={setChPush} testId="cl-ch-push" color="text-primary"/>
                    </div>
                </div>

                {/* Target */}
                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Send To</label>
                    <div className="flex gap-3">
                        <TargetBtn active={targetMode === "all"} onClick={() => setTargetMode("all")}
                            icon={<Users className="w-3.5 h-3.5"/>} label="All Tenants" testId="cl-target-all"/>
                        <TargetBtn active={targetMode === "selected"} onClick={() => setTargetMode("selected")}
                            icon={<Building2 className="w-3.5 h-3.5"/>} label="Select Tenants" testId="cl-target-selected"/>
                    </div>
                </div>

                {/* Tenant picker */}
                {targetMode === "selected" && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                Select Tenants ({selectedTenants.size} selected)
                            </label>
                            {selectedTenants.size > 0 && (
                                <button onClick={() => setSelectedTenants(new Set())} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="cl-clear-tenants">
                                    Clear all
                                </button>
                            )}
                        </div>
                        <input type="text" placeholder="Search firms…" value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
                            className="w-full bg-background border border-border px-3 py-2 text-xs focus:border-primary outline-none"/>
                        <div className="bg-background border border-border max-h-52 overflow-y-auto">
                            {tenantsLoading && <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 inline-block animate-spin"/></div>}
                            {!tenantsLoading && filteredTenants.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">No tenants found</div>}
                            {filteredTenants.map(t => {
                                const id = t.id || t._id;
                                const sel = selectedTenants.has(id);
                                return (
                                    <div key={id} onClick={() => toggleTenant(id)}
                                        data-testid={`cl-tenant-${id}`}
                                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-border/50 hover:bg-muted transition-colors ${sel ? "bg-primary/5" : ""}`}>
                                        <input type="checkbox" checked={sel} readOnly className="accent-primary pointer-events-none"/>
                                        <div>
                                            <div className="text-xs text-foreground">{t.name || t.firm_name || id}</div>
                                            <div className="text-[10px] text-muted-foreground">{t.owner_email || t.plan_slug || ""}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <button onClick={dispatch} disabled={dispatching || (!chWA && !chEmail && !chPush)}
                        data-testid="cl-dispatch-btn"
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary/80 disabled:opacity-30">
                        {dispatching ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" strokeWidth={1.5}/>}
                        {dispatching ? "Dispatching…" : targetMode === "all" ? "Send to All Tenants" : `Send to ${selectedTenants.size || 0} Tenant(s)`}
                    </button>
                </div>
            </div>

            {/* Results */}
            {results && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="overline text-primary">Dispatch Results — {results.date}</div>
                        <span className="text-xs text-muted-foreground">{results.ok_count}/{results.tenant_count} tenants reached</span>
                    </div>
                    <div className="bg-muted border border-border overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-background/30 text-muted-foreground uppercase tracking-[0.16em] text-[10px] sticky top-0">
                                <tr>
                                    <th className="text-left p-3">Firm</th>
                                    {results.results[0]?.channels?.push !== undefined && <th className="text-center p-3">Push</th>}
                                    {results.results[0]?.channels?.email !== undefined && <th className="text-center p-3">Email</th>}
                                    {results.results[0]?.channels?.whatsapp !== undefined && <th className="text-center p-3">WhatsApp</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {results.results.map((r, i) => (
                                    <tr key={i} className="border-t border-border" data-testid={`cl-result-${r.tenant_id}`}>
                                        <td className="p-3">
                                            <div className="text-foreground">{r.firm}</div>
                                            <div className="text-muted-foreground font-mono text-[10px]">{r.tenant_id}</div>
                                        </td>
                                        {r.channels?.push !== undefined && (
                                            <td className="p-3 text-center">
                                                <StatusIcon ok={r.channels.push.ok}/>
                                                {r.channels.push.sent !== undefined && <span className="text-muted-foreground ml-1">{r.channels.push.sent} sent</span>}
                                            </td>
                                        )}
                                        {r.channels?.email !== undefined && (
                                            <td className="p-3 text-center">
                                                <StatusIcon ok={r.channels.email.ok} reason={r.channels.email.reason}/>
                                            </td>
                                        )}
                                        {r.channels?.whatsapp !== undefined && (
                                            <td className="p-3 text-center">
                                                <StatusIcon ok={r.channels.whatsapp.ok} reason={r.channels.whatsapp.reason}/>
                                                {r.channels.whatsapp.provider && <span className="text-muted-foreground ml-1 text-[10px]">{r.channels.whatsapp.provider}</span>}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function ChannelToggle({ icon, label, checked, onChange, testId, color }) {
    return (
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
                data-testid={testId} className="accent-primary"/>
            <span className={color}>{icon}</span>
            {label}
        </label>
    );
}

function TargetBtn({ active, onClick, icon, label, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className={`flex items-center gap-2 px-4 py-2 text-xs border transition-colors
                ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"}`}>
            {icon}{label}
        </button>
    );
}

function StatusIcon({ ok, reason }) {
    if (ok) return <CheckCircle2 className="w-4 h-4 inline-block text-emerald-400" title="Success"/>;
    return <span title={reason || "Failed"}><XCircle className="w-4 h-4 inline-block text-red-400"/></span>;
}

function Tile({ label, value, accent }) {
    return (
        <div className="bg-muted border border-border p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            <div className={`text-2xl font-light ${accent || "text-foreground"}`}>{value}</div>
        </div>
    );
}

